<template>
    <div data-testid="step-phoneNumber" class="container">
        <h1 class="container__main-header">
            {{ $t(`${TRANSLATION_KEY}.documentUploadHeader`) }}
        </h1>
        <p class="container__subheader t-center t-bold upload_subheader">
            {{ $t(`${TRANSLATION_KEY}.chooseOptionDescription`) }}
        </p>
        <p class="container__subheader t-center upload_desc">
            {{ $t(`${TRANSLATION_KEY}.mobileRecommendedInfo`) }}
        </p>

        <p v-if="store.state.residenceCountry == 'PL'" class="warning">
            {{ $t(`${TRANSLATION_KEY}.headerWarning`) }}
        </p>
        <p v-if="store.state.residenceCountry == 'ES'" class="warning">
            {{ $t(`${TRANSLATION_KEY}.spanishHeaderWarning`) }}
        </p>

        <RecommendedPopup
            :is-open="openRecommendedPopup"
            @close="handleClosePopup"
        />

        <div class="recommended_path_container">
            <div class="recommended_header">
                <div class="recommended_header_lines">
                    <div class="line" />
                    <div class="recommended_header_center">
                        <img
                            v-if="!UtilityHelper.isOandaPath()"
                            src="@/assets/phone_icon.svg"
                            alt="phone icon"
                        />
                        <img
                            v-if="UtilityHelper.isOandaPath()"
                            src="@/assets/phone_icon_oanda.svg"
                            alt="phone icon"
                        />
                        <span>{{
                            $t(`${TRANSLATION_KEY}.recommendedPathLabel`)
                        }}</span>
                    </div>
                    <div class="line" />
                </div>
            </div>
            <div
                class="mobile-options"
                role="tablist"
                aria-label="Upload method"
            >
                <button
                    id="phone-number-tab-qr"
                    type="button"
                    role="tab"
                    class="mobile-options__tab"
                    :class="{ active: qrCode }"
                    :aria-selected="qrCode"
                    aria-controls="phone-number-panel-qr"
                    @click="setOption(true)"
                >
                    {{ $t(`${TRANSLATION_KEY}.selectMethodOne`) }}
                </button>
                <button
                    id="phone-number-tab-sms"
                    type="button"
                    role="tab"
                    class="mobile-options__tab"
                    :class="{ active: !qrCode }"
                    :aria-selected="!qrCode"
                    aria-controls="phone-number-panel-sms"
                    @click="setOption(false)"
                >
                    {{ $t(`${TRANSLATION_KEY}.selectMethodTwo`) }}
                </button>
            </div>
            <div
                v-show="qrCode"
                id="phone-number-panel-qr"
                class="qr-code"
                role="tabpanel"
                aria-labelledby="phone-number-tab-qr"
            >
                <p class="container__subheader t-center t-bold">
                    {{ $t(`${TRANSLATION_KEY}.qrCodeInstructions`) }}
                </p>
                <div v-if="localQrUrl" class="qr-code__img-container">
                    <qrcode-vue
                        :value="localQrUrl"
                        :size="size"
                        level="H"
                        render-as="svg"
                    />
                </div>
                <div v-else>
                    <Loader class="qr-code-_loader active" />
                </div>
            </div>
            <div
                v-show="!qrCode"
                id="phone-number-panel-sms"
                class="phone-number"
                role="tabpanel"
                aria-labelledby="phone-number-tab-sms"
            >
                <p class="container__subheader t-center t-bold">
                    {{ $t(`${TRANSLATION_KEY}.phoneNumberParagraph`) }}
                </p>
                <form v-if="!isSmsSent" @submit.prevent="function () {}">
                    <div
                        class="row row--account-validation-get-data"
                        :style="{ 'margin-bottom': isSmsSent ? '0' : '-25px' }"
                    >
                        <PhoneField
                            ref="phoneNumberInput"
                            v-model="phone"
                            name="phoneNumber"
                            cy-data="phone-number"
                            :placeholder="
                                $t(`${TRANSLATION_KEY}.phoneNumberPlaceholder`)
                            "
                            @update:model-value="
                                ($event) => updatePhone($event)
                            "
                        />
                    </div>
                    <RecaptchaButton
                        :action="Action.MOBILE_SMS"
                        :data="formData"
                        :button-value="
                            $t(`${TRANSLATION_KEY}.confirmPhoneNumber`)
                        "
                        class="button submit-button"
                        :style="{ top: isSmsSent ? '0' : '25px' }"
                        :disabled="!formData.phoneNumber"
                        @success-submit="isSmsSent = true"
                        @click="
                            () =>
                                amplitudeSendEvent(
                                    AmplitudeAnalyticsEvents.proofOfIdentity
                                        .PROOF_OF_IDENTITY_SMS
                                )
                        "
                    />
                </form>
                <!--- Second form is visible when first form was sent with data like (directional | phone number) to backend --->
                <form
                    v-if="isSmsSent"
                    id="confirm-phone"
                    @submit.prevent="null"
                >
                    <div class="row row--account-validation-get-data">
                        <div
                            class="row row--account-validation-get-data__phone-number"
                        >
                            <PhoneField
                                ref="phoneNumberInput"
                                v-model="phone"
                                name="phoneNumber"
                                cy-data="phone-number"
                                :placeholder="
                                    $t(
                                        `${TRANSLATION_KEY}.phoneNumberPlaceholder`
                                    )
                                "
                                @update:model-value="
                                    ($event) => updatePhone($event)
                                "
                            />
                        </div>
                        <div class="row--account-validation-get-sms-code">
                            <div
                                class="row--account-validation-get-sms-code__input"
                            >
                                <Field
                                    v-model="formData.phoneVerificationCode2"
                                    type="text"
                                    name="phoneVerificationCode2"
                                    :placeholder="
                                        $t(`${TRANSLATION_KEY}.smsCode`)
                                    "
                                    cy-data="sms-code"
                                    :is-numeric="true"
                                >
                                    <span>
                                        {{ $t(`${TRANSLATION_KEY}.smsCode`) }}
                                    </span>
                                </Field>
                            </div>
                        </div>
                    </div>
                    <RecaptchaButton
                        :action="Action.MOBILE_SMS_CODE"
                        :data="formData"
                        :button-value="$t(`${TRANSLATION_KEY}.next`)"
                        class="button submit-button"
                        :disabled="!formData.phoneVerificationCode2.length"
                        :style="{ 'margin-bottom': isSmsSent ? '-11px' : '0' }"
                    />
                    <RecaptchaButton
                        :action="Action.MOBILE_SMS"
                        :data="formData"
                        button-type="button"
                        class="resend-code"
                        @success-submit="isSmsSent = true"
                    >
                        {{ $t(`${TRANSLATION_KEY}.didntGetSmsOne`) }}
                        <strong
                            >{{
                                $t(`${TRANSLATION_KEY}.didntGetSmsTwo`)
                            }}
                            &raquo;</strong
                        >
                    </RecaptchaButton>
                    <button
                        type="button"
                        class="fix-phone-number"
                        @click="isSmsSent = false"
                    >
                        {{ $t(`${TRANSLATION_KEY}.wrongPhoneOne`) }} -
                        <strong
                            >{{
                                $t(`${TRANSLATION_KEY}.wrongPhoneTwo`)
                            }}
                            &raquo;</strong
                        >
                    </button>
                </form>
            </div>
            <p class="t-center">
                {{ $t(`${TRANSLATION_KEY}.redirectToDesktopInfo`) }}
            </p>
        </div>

        <div class="alternate-path-header">
            <img src="@/assets/alternate_path_icon.svg" alt="computer" />
            <span>{{ $t(`${TRANSLATION_KEY}.alternatePathLabel`) }}</span>
        </div>

        <Acordeon :title="$t(`${TRANSLATION_KEY}.checkOptions`)">
            <button
                type="button"
                data-testid="btn-phoneNumber-uploadFromThisDevice"
                class="alternate-button"
                @click="handleOpenPopup"
            >
                <span>{{
                    $t(`${TRANSLATION_KEY}.uploadFromThisDeviceButton`)
                }}</span>
            </button>
            <button
                type="button"
                data-testid="btn-phoneNumber-skip"
                class="navigation-link"
                @click="handleSkipUpload"
            >
                {{ $t(`${TRANSLATION_KEY}.uploadLaterButton`) }} »
            </button>
        </Acordeon>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    onMounted,
    onBeforeUnmount,
    reactive,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import QrcodeVue from 'qrcode.vue';
import RecaptchaButton from '@/components/basic/buttons/RecaptchaSubmitButton.vue';
import Field from '@/components/basic/fields/Field.vue';
import PhoneField from '@/components/compositeFields/PhoneField.vue';
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import Loader from '../basic/Loader.vue';
import { Action } from '@/constants/ajax';
import { SET_PROGRESS } from '@/constants/mutation-types';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import Acordeon from '../basic/Acordeon.vue';
import RecommendedPopup from '../basic/RecommendedPopup.vue';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';
import { PROGRESS_BAR_TIMES } from '@/constants/progress-bar-times';

export default defineComponent({
    name: 'PhoneNumber',
    components: {
        Field,
        SubmitButton,
        PhoneField,
        RecaptchaButton,
        QrcodeVue,
        Loader,
        Acordeon,
        RecommendedPopup,
    },
    setup() {
        const isExpanded = ref(false);
        const isSmsSent = ref(false);
        const phoneChanged = ref(false);
        const openRecommendedPopup = ref(false);

        const phone = reactive({
            phone: '',
            phonePrefix: '',
        });

        const formData = reactive({
            phoneNumber: '',
            phoneNumberPrefix: '',
            phoneVerificationCode2: '',
            recaptchaToken: '',
        });

        const isDisabledButton = ref(true);
        const TRANSLATION_KEY = ref('steps.PhoneNumber');
        const size = ref(200);
        const qrCode = ref(true);
        const localQrUrl = ref(false as string | boolean);
        const interval = ref<number | null>(0);

        const phoneNumberInput = ref<InstanceType<typeof PhoneField> | null>(
            null
        );

        const store = useStore();
        const route = useRoute();

        const getAction = (): Action => {
            let action = Action.DESKTOP_SMS;
            if (route.name === 'mobileSms') {
                action = Action.MOBILE_SMS;
            }
            return action;
        };

        const updatePhone = (value: any) => {
            formData.phoneNumber = value.phone;
            formData.phoneNumberPrefix = value.phonePrefix;
        };

        const setOption = (isQrCode: boolean) => {
            qrCode.value = isQrCode;
            if (qrCode.value) {
                store.state.analyticsService.sendStepEvent('uploadQR');
            } else {
                store.state.analyticsService.sendStepEvent('uploadPhone');
            }
        };

        const checkWaitStatus = () => {
            interval.value = window.setInterval(async () => {
                const ajaxService = store.state.ajaxService;
                const data = await ajaxService.post(
                    ajaxService.RESOURCES[Action.MOBILE_QR_CODE_SCAN],
                    {
                        token: ajaxService.getToken(),
                    }
                );
                if (data && data.isQrCodeScan === true) {
                    if (interval.value !== null) {
                        clearInterval(interval.value);
                    }
                    amplitudeSendEvent(
                        AmplitudeAnalyticsEvents.proofOfIdentity
                            .PROOF_OF_IDENTITY_QR
                    );
                    store.state.navigationService.goToRoute(
                        'mobileDesktopIsCompleted'
                    );
                }
            }, 4000);
        };

        const handleDocumentClick = (evt: MouseEvent) => {
            const target = evt.target as HTMLElement;
            if (!phoneNumberInput.value) return;

            const refEl = phoneNumberInput.value.$el;
            if (isExpanded.value) {
                const classes = [
                    'vue-tel-input',
                    'vti__dropdown',
                    'vti__input',
                    'vti__selection',
                ];

                if (
                    target &&
                    !classes.some((c) => target.classList.contains(c))
                ) {
                    refEl.classList.remove('modifier-vue-tel-input');
                    refEl.classList.add('default-vue-tel-input');
                    isExpanded.value = false;
                }

                if (target.classList.contains('vti__input')) {
                    refEl.classList.remove('modifier-vue-tel-input');
                    refEl.classList.add('default-vue-tel-input');
                    isExpanded.value = false;
                }
            }
        };

        const refreshQrCode = async () => {
            const ajaxService = store.state.ajaxService;
            const data = await ajaxService.post(
                ajaxService.PROXY_ROUTE + 'form-pl/mobile-qr-code-url',
                {
                    token: ajaxService.getToken(),
                }
            );
            if (data && data.mobileQrUrl) {
                localQrUrl.value = data.mobileQrUrl;
            }
        };

        onMounted(() => {
            store.commit(SET_PROGRESS, PROGRESS_BAR_TIMES.PHONE_NUMBER);

            const mobileQrUrl = store.state.response?.mobileQrUrl;

            if (mobileQrUrl) {
                localQrUrl.value = mobileQrUrl;
            } else {
                refreshQrCode();
            }

            checkWaitStatus();
            document.addEventListener('click', handleDocumentClick);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleDocumentClick);
            if (interval.value) clearInterval(interval.value);
        });

        const handleSkipUpload = () => {
            store.state.navigationService.goToRoute('personalDataConfirm');
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.proofOfIdentity.PROOF_OF_IDENTITY_SKIP
            );
        };

        const handleOpenPopup = () => {
            openRecommendedPopup.value = true;
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.proofOfIdentity
                    .PROOF_OF_IDENTITY_UPLOAD_FROM_DEVICE
            );
        };

        const handleClosePopup = () => {
            openRecommendedPopup.value = false;
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.proofOfIdentity
                    .PROOF_OF_IDENTITY_POPUP_RETURN
            );
        };

        return {
            isExpanded,
            isSmsSent,
            phoneChanged,
            phone,
            formData,
            isDisabledButton,
            TRANSLATION_KEY,
            Action,
            phoneNumberInput,
            updatePhone,
            getAction,
            qrCode,
            setOption,
            localQrUrl,
            size,
            store,
            Loader,
            UtilityHelper,
            openRecommendedPopup,
            handleSkipUpload,
            handleOpenPopup,
            handleClosePopup,
            amplitudeSendEvent,
            AmplitudeAnalyticsEvents,
        };
    },
});
</script>

<style lang="scss" scoped>
.step-confirm-phone__info {
    margin-top: 0;
    margin-bottom: 30px;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
}

.upload_subheader {
    font-size: 18px;
    margin-bottom: 15px !important;
}

.upload_desc {
    margin-top: 0;
}

.mobile-options {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    max-width: 294px;
    height: 34px;
    border: 1px solid #00214a;
    margin: 0 auto;
    min-height: 40px;
    margin-top: 15px;

    &__tab {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 0 10px;
        border: none;
        background: transparent;
        font-size: 13px;
        font-weight: 600;
        color: #00214a;
        cursor: pointer;
        text-align: center;

        &.active {
            background: #00214a;
            color: #fff;
        }

        &:focus-visible {
            outline: 2px solid #192241;
            outline-offset: 2px;
            position: relative;
            z-index: 1;
        }
    }
}

.qr-code {
    &__img-container {
        padding: 20px;
        width: max-content;
        margin: 0 auto;
        border: 1px solid #dddee3;
    }

    img {
        display: block;
        margin: 0 auto;
        width: 250px;
        height: 250px;
    }
}

.qr-code-_loader {
    position: relative;
    transform: unset;
    -webkit-transform: unset;
}

.recommended_path_container {
    position: relative;
    border: 1px solid #af1919;
    padding: 40px 30px 30px;
    border-top: none;

    &::before {
        content: '';
        position: absolute;
        width: 15%;
        height: 1px;
        background-color: #af1919;
        top: 0;
        left: 0;
    }

    &::after {
        content: '';
        position: absolute;
        width: 15%;
        height: 1px;
        background-color: #af1919;
        top: 0;
        right: 0;
    }

    .recommended_header {
        position: absolute;
        top: -1rem;
        left: 50%;
        transform: translateX(-50%);
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 0.25rem;

        .recommended_header_lines {
            display: flex;
            width: 100%;
            align-items: center;

            .line {
                flex: 1;
            }

            .recommended_header_center {
                display: flex;
                align-items: center;
                max-width: 75%;
                padding: 0 1rem;
                text-align: center;
                font-weight: 700;
                font-size: 18px;
                white-space: normal;
                word-break: break-word;
            }
        }
    }

    div.body.oanda & {
        border-color: #00d37e;

        &::before,
        &::after {
            background-color: #00d37e;
        }
    }
}

.alternate-path-header {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 20px 0px;

    span {
        font-weight: 700;
        font-size: 18px;
    }
}

.alternate-button {
    border: 1px solid #00214a;
    background: transparent;
    text-align: center;
    padding: 20px;
    font-size: 18px;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 30px;
    cursor: pointer;
    opacity: 0.8;
    width: 100%;
    &:hover {
        opacity: 1;
    }
}

.warning {
    text-align: center;
    margin-bottom: 30px;
}
</style>
